// Theme types
export type ThemeMode = 'light' | 'dark';

// User types - Azure AD Roles
export const UserRole = {
  ProcurementOfficer: 'ProcurementOfficer',
  AuthorityAdmin: 'AuthorityAdmin',
  MmrOversight: 'MmrOversight',
  SystemAdmin: 'SystemAdmin',
} as const;

export type UserRole = typeof UserRole[keyof typeof UserRole];

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  authorityId?: number;
  department?: string;
  createdAt: Date;
  lastLogin?: Date;
}

// Procurement types
export const ProcurementType = {
  OPEN: 'open',
  RESTRICTED: 'restricted',
  NEGOTIATED: 'negotiated',
  COMPETITIVE_DIALOGUE: 'competitive_dialogue',
  INNOVATION_PARTNERSHIP: 'innovation_partnership',
  SIMPLIFIED_BELOW_THRESHOLD: 'simplified_below_threshold',
} as const;

export type ProcurementType = typeof ProcurementType[keyof typeof ProcurementType];

export interface Procurement {
  id: string;
  title: string;
  type: ProcurementType;
  contractorName: string;
  estimatedValue?: number;
  currency: string;
  publicationDate: Date;
  deadline?: Date;
  description?: string;
  documentUrl?: string;
}

// Validation types
export const ValidationStatus = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
} as const;

export type ValidationStatus = typeof ValidationStatus[keyof typeof ValidationStatus];

export const Severity = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low',
  INFO: 'info',
} as const;

export type Severity = typeof Severity[keyof typeof Severity];

export const FindingCategory = {
  FORMAL: 'formal',
  LEGAL: 'legal',
  FINANCIAL: 'financial',
  TECHNICAL: 'technical',
  PROCEDURAL: 'procedural',
  OTHER: 'other',
} as const;

export type FindingCategory = typeof FindingCategory[keyof typeof FindingCategory];

export type FindingFeedback =
  | 'correct'
  | 'incorrect'
  | 'partially_correct'
  | 'not_applicable';

export interface Finding {
  id: string;
  layer: string;
  severity: Severity;
  category: FindingCategory;
  title: string;
  description: string;
  location?: string;
  recommendation?: string;
  reference?: string;
  userFeedback?: FindingFeedback | null;
  userFeedbackComment?: string | null;
  userFeedbackAt?: string | null;
  userFeedbackBy?: number | null;
}

export interface Validation {
  id: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  uploadedAt: Date;
  uploadedBy: string;
  status: ValidationStatus;
  procurementType?: ProcurementType;
  findings: Finding[];
  summary?: {
    totalFindings: number;
    highCount: number;
    mediumCount: number;
    lowCount: number;
    infoCount: number;
  };
  completedAt?: Date;
  errorMessage?: string;
}

// API response types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
  timestamp: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// Filter and sort types
export interface ValidationFilters {
  status?: ValidationStatus[];
  severity?: Severity[];
  dateFrom?: Date;
  dateTo?: Date;
  searchQuery?: string;
}

export interface SortOptions {
  field: string;
  direction: 'asc' | 'desc';
}

// Upload types
export interface UploadProgress {
  fileName: string;
  progress: number;
  status: 'uploading' | 'processing' | 'completed' | 'error';
  error?: string;
}
